import React, { useCallback, useState } from 'react';

import { Box } from '@betterment/samba-react';
import { Button, Flex, FormikRadioInput, Label, Modal, Text, theme } from '@betterment/style-closet-react';
import styled from '@emotion/styled';
import VisuallyHidden from '@reach/visually-hidden';
import { min } from 'es-toolkit/compat';
import { useFormikContext } from 'formik';
import ReactCrop, { Crop } from 'react-image-crop';
import * as Yup from 'yup';

import 'react-image-crop/lib/ReactCrop.scss';
import { CustomImageIconPair, UploadImageIconPair } from './GoalImageIconPair';

import { fileIsAnimatedPNG, fileToDataURLString } from '../../models/filereader';
import { toYupValidationErrorString } from '../../to-yup-validaiton-error';

import { GoalImageProps } from './index';

const SelectedOptionContainer = styled(Flex)`
  cursor: pointer;
  align-items: center;
  border-radius: 11px;
  min-height: 170px;
  width: 120px;
`;

const FILE_SIZE = 8_000_000; // approximately under 8MB
const SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/png'];

const isValidPNGOrJPG = async (file: File) => {
  if (file.type !== 'image/png') return true;
  const fileString = await fileToDataURLString(file);
  return !fileIsAnimatedPNG(fileString);
};

const fileSchema = Yup.mixed<File>()
  .required('A file is required')
  .test('fileSize', 'File too large', value => value?.size <= FILE_SIZE)
  .test('fileSize', 'File too small', value => value?.size > 1)
  .test('fileFormat', 'Unsupported format', value => SUPPORTED_FORMATS.includes(value?.type))
  .test('notAPNG', 'Multiple page PNG is not supported', file => isValidPNGOrJPG(file));

const ImageUploader: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { values, setFieldValue, setFieldError, setFieldTouched } = useFormikContext<{
    customImageData: string;
    imageType: GoalImageProps['name'];
  }>();

  const [isModalOpen, setModalOpen] = useState(false);
  const closeModal = () => {
    setModalOpen(false);
  };

  const [uncroppedImageData, setUncroppedImageData] = useState<string>('');
  const [uncroppedImage, setUncroppedImage] = useState<HTMLImageElement | undefined>(undefined);
  const [crop, setCrop] = useState<Crop>({ unit: '%', x: 10, y: 10, width: 80, height: 80, aspect: 1 });

  const handleCustomImageDataChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.currentTarget.files && event.currentTarget.files.length > 0) {
      const file = event.currentTarget.files[0];
      event.currentTarget.value = '';

      try {
        await fileSchema.validate(file);

        const fileString = await fileToDataURLString(file);

        setUncroppedImageData(fileString);
        void setFieldValue('imageType', 'custom');
        setModalOpen(true);
      } catch (error) {
        setFieldError('customImageData', toYupValidationErrorString(error));
        void setFieldTouched('customImageData', true, false);
      }
    }
  };

  const onCropperImageLoaded = useCallback((image: HTMLImageElement) => {
    setUncroppedImage(image);

    const DEFAULT_CROP_RATIO = 0.8;
    const initialCropSize = min([image.width, image.height])! * DEFAULT_CROP_RATIO;

    setCrop({
      unit: 'px',
      x: (image.width - initialCropSize) / 2,
      y: (image.height - initialCropSize) / 2,
      width: initialCropSize,
      height: initialCropSize,
      aspect: 1,
    });
    return false;
  }, []);

  const generateCroppedDataString = (image: HTMLImageElement, selectedCrop: Crop) => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext('2d');
    const pixelRatio = window.devicePixelRatio || 1;

    if (!ctx) {
      return null;
    }

    canvas.width = selectedCrop.width! * pixelRatio;
    canvas.height = selectedCrop.height! * pixelRatio;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      selectedCrop.x! * scaleX,
      selectedCrop.y! * scaleY,
      selectedCrop.width! * scaleX,
      selectedCrop.height! * scaleY,
      0,
      0,
      selectedCrop.width!,
      selectedCrop.height!
    );

    return canvas.toDataURL('image/png');
  };

  const submitCroppedImage = () => {
    if (uncroppedImage) {
      void setFieldValue('customImageData', generateCroppedDataString(uncroppedImage, crop));
      setModalOpen(false);
    }
  };

  return (
    <>
      {isModalOpen && (
        <Modal contentWidth={['100%', '443px']} closeHandler={closeModal} label="Crop your image">
          <Box
            css={{
              padding: 'space.200',
            }}
          >
            <Text as="h2">Crop your image</Text>
            <Box>
              {uncroppedImageData && (
                <ReactCrop
                  src={uncroppedImageData}
                  onImageLoaded={onCropperImageLoaded}
                  crop={crop}
                  onChange={c => setCrop(c)}
                  imageAlt="Crop selection"
                />
              )}
            </Box>
            <Button onClick={submitCroppedImage} aria-label="Submit cropped image">
              Looks great
            </Button>
            <Button variant="secondary" onClick={closeModal} ml="4" aria-label="Cancel image upload">
              Cancel
            </Button>
          </Box>
        </Modal>
      )}
      <Label
        id="goal-image-upload-label"
        htmlFor="customImageData"
        m="0"
        p="0"
        backgroundColor="inherit"
        display="inline-block"
      >
        <VisuallyHidden>
          <input
            type="file"
            id="customImageData"
            name="customImageData"
            onChange={handleCustomImageDataChange}
            hidden={true}
            accept=".png, .jpg, .jpeg"
            aria-labelledby="goal-image-upload-label goal-image-selector"
          />
          <input
            type="text"
            id="addGoalCustomImageData"
            name="addGoalCustomImageData"
            hidden={true}
            aria-labelledby="goal-image-upload-label goal-image-selector"
            value={values.customImageData?.split(',')[1] || ''}
            readOnly={true}
          />
          <FormikRadioInput
            id="goal-image-custom"
            aria-labelledby="goal-image-custom-label goal-image-selector"
            name="imageType"
            value="custom"
          />
          <Text color={theme.colors.bmtBlue}>Upload photo</Text>
        </VisuallyHidden>
        <SelectedOptionContainer
          flexDirection="column"
          p="1"
          bg={values.imageType === 'custom' ? 'bmtBlue' : undefined}
          mx="auto"
        >
          {values.customImageData && <CustomImageIconPair file={values.customImageData} />}

          {!values.customImageData && <UploadImageIconPair />}
          {values.imageType === 'custom' && (
            <Flex mt="8">
              <Text as="p" mb="1" color="bmtWhite">
                {values.customImageData ? 'Change' : 'Selected'}
              </Text>
            </Flex>
          )}
        </SelectedOptionContainer>
      </Label>
    </>
  );
};

export default ImageUploader;
